// ✅ CriticalPathHorizontal1 - versión FINAL SIN DESFASES
import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
  useDeferredValue,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Chip,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";

import { socket } from "../../socket";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

import { useProveedorPermissions } from "../../hooks/useProveedorPermissions"; // CHINA

import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  getCriticalPathHorizontal1,
  getCriticalPathClosedEntities,
  setCriticalPathEntityClosed,
} from "../../api/Estimates/estimatesApi";

import {
  getNotasByEntityWithUser,
  getNotasCountAll,
} from "../../api/CriticalPath/criticalPathNotasApi";

import {
  getFileContent,
  getMuestraFiles,
  getMuestraFileContent,
} from "../../api/Proyectos/projectFilesApi";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import CriticalPathDrawer from "./CriticalPathDrawer";
import CriticalPathInlineGanttAligned from "./CriticalPathInlineGanttAligned";
import {
  collectGanttBarDates,
  dateExtent,
  extentToLineXs,
} from "./ganttBarSeries";

import {
  CP_SORT_FIELD_OPTIONS,
  sortCriticalPathRows,
} from "./criticalPathSortUtils";

import {
  getEntityInitial,
  getSupplierColor,
  getClientColor,
} from "./criticalPathAvatars";

import { getCpDesignDisplay } from "./criticalPathDesignDisplay";
import { withLegacyLogisticsFlag } from "./criticalPathEntityUtils";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";

import NotasBadge from "./NotasBadge";
import {
  getCpNotasCountKeyFromApiRow,
  getCpNotasEntityKey,
} from "../../utils/cpNotasEntityKey";

import CpEntityChat, {
  getCpChatMetaKey,
  getCpChatMetaKeyFromRow,
} from "./CpEntityChat";
import CpChatDot from "./CpChatDot";
import { getEntityChatSummary } from "../../api/Chat/entityChatApi";
import { setBrowserTabBadge } from "../../utils/browserTabBadge";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pageMobileCardSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";

import {
  ChatBubbleOutline, // comentarios
  LocalShippingOutlined, // envíos, booking, forwarder, logística
  FactoryOutlined, // fábrica, producción
  ScienceOutlined, // laboratorio, test
  CheckCircleOutline, // aprobado, done
  SearchOutlined, // inspección
  EventOutlined, // fechas (ETD, ETA, CUT-OFF…)
  DescriptionOutlined, // documentos, telex, BL, código cliente
  RequestQuoteOutlined, // PO, referencias financieras
  Inventory2Outlined, // volumen, peso, cajas
  AssignmentTurnedInOutlined, // documentos aprobados / finalizados
  BookmarkBorderOutlined, // reserva, booking
  StoreOutlined, // fábrica general
  DoneAllOutlined, // proceso completado
  CalendarMonthOutlined, // fechas genéricas
  RadioButtonUnchecked, // fallback
  VisibilityOffOutlined,
  VisibilityOutlined,
  LocalShippingOutlined as LegacyLogisticsIcon,
  PersonOutlined,
} from "@mui/icons-material";

const parseTaskDate = (v) => {
  if (!v || v === "—" || v === "— —") return null;
  const d = dayjs(v, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
  return d.isValid() ? d : null;
};

const ICON_MAP = {
  // 📞 Comunicación
  "SMS comments": ChatBubbleOutline,
  "SSS Fitting comments": ChatBubbleOutline,

  // 🧵 Envíos y fittings
  "SSS Fitting send": LocalShippingOutlined,
  "PP send": LocalShippingOutlined,
  "TOP sple send": LocalShippingOutlined,
  "TOP approved": CheckCircleOutline,

  // 🏭 Producción y materiales
  "Fabrics and trims In-house": FactoryOutlined,
  "Production Starts": FactoryOutlined,
  "Ok production Received": DoneAllOutlined,

  // 🔬 Laboratorio / pruebas
  "Testing send to lab": ScienceOutlined,

  // 🔍 Control e inspección
  "Inspection date": SearchOutlined,

  // 📆 Fechas clave
  ExFD: EventOutlined,
  ETDF: EventOutlined,
  "CUT-OFF": EventOutlined,
  ETD: EventOutlined,
  DD: EventOutlined,
  ETA: EventOutlined,

  // 📦 Logística
  "N Cartons": Inventory2Outlined,
  "Gross Weight Kgs": Inventory2Outlined,
  "Total Volume cbm": Inventory2Outlined,
  "Shipment Method": LocalShippingOutlined,
  Forwarder: LocalShippingOutlined,
  "Booking Done": BookmarkBorderOutlined,
  "Shipment Code Customer": DescriptionOutlined,
  "BL AWB Nr": DescriptionOutlined,

  // 📄 Documentación y aprobación
  "Documents Approved": AssignmentTurnedInOutlined,
  "Original Telex Docs Semt": DescriptionOutlined,

  // 💰 Finanzas / órdenes de compra
  POReference: RequestQuoteOutlined,
  PODate: CalendarMonthOutlined,
  "PO Date": CalendarMonthOutlined,

  // 🏭 Fábricas
  Factory: StoreOutlined,
};

// Cache global para fotos
const imagesCache = new Map();
const imagesPromiseCache = new Map();
const MAX_IMAGE_LOAD_CONCURRENCY = 4;
let activeImageLoads = 0;
const imageLoadQueue = [];

const releaseImageLoadSlot = () => {
  activeImageLoads -= 1;
  drainImageLoadQueue();
};

const drainImageLoadQueue = () => {
  while (
    activeImageLoads < MAX_IMAGE_LOAD_CONCURRENCY &&
    imageLoadQueue.length > 0
  ) {
    const next = imageLoadQueue.shift();
    activeImageLoads += 1;

    next
      .task()
      .then(next.resolve)
      .catch(next.reject)
      .finally(releaseImageLoadSlot);
  }
};

const runQueuedImageLoad = (task) =>
  new Promise((resolve, reject) => {
    imageLoadQueue.push({ task, resolve, reject });
    drainImageLoadQueue();
  });

function parseIdPresupuestoFromRowId(rowId) {
  if (rowId == null) return null;
  const m = String(rowId).match(/^PRESUPUESTO_(\d+)$/i);
  return m ? Number(m[1]) : null;
}

const ROW_HEIGHT = 58;
const GANTT_LINE_OFFSET = 8;
const EXPECTED_LINE_COLOR = "#9e9e9e";
const HEADER_HEIGHT = 56;
const INITIAL_RENDER_ROWS = 30;
const RENDER_CHUNK_ROWS = 30;
const MAX_AUTO_RENDER_ROWS = 120;

const getOrLoadImages = async (cacheKey, loader) => {
  if (imagesCache.has(cacheKey)) return imagesCache.get(cacheKey);

  if (imagesPromiseCache.has(cacheKey)) {
    return imagesPromiseCache.get(cacheKey);
  }

  const loadPromise = (async () => {
    try {
      const result = await loader();
      imagesCache.set(cacheKey, result);
      return result;
    } catch {
      imagesCache.set(cacheKey, []);
      return [];
    } finally {
      imagesPromiseCache.delete(cacheKey);
    }
  })();

  imagesPromiseCache.set(cacheKey, loadPromise);
  return loadPromise;
};

const PhotosCell = React.memo(function PhotosCell({ row, onOpenImage }) {
  const [images, setImages] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const cellRef = useRef(null);

  const cacheKey = row.ProductId
    ? `P_${row.NumeroPresupuesto}`
    : `M_${row.ID_MUESTRA}`;

  useEffect(() => {
    const el = cellRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "300px 0px", threshold: 0.01 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || images !== null) return;

    const load = async () => {
      const formatted = await getOrLoadImages(cacheKey, async () =>
        runQueuedImageLoad(async () => {
          if (row.ProductId) {
            const files = await getFileContent(row.NumeroPresupuesto);
            return files.slice(0, 1).map((file) => ({
              url: `data:${file.mimeType};base64,${file.data}`,
              fileName: file.fileName,
            }));
          }

          if (row.ID_MUESTRA) {
            const files = await getMuestraFiles(row.ID_MUESTRA);
            const firstFiles = files.slice(0, 1);
            return Promise.all(
              firstFiles.map(async (file) => {
                const content = await getMuestraFileContent(file.path);
                return {
                  url: `data:${content.mimeType};base64,${content.data}`,
                  fileName: file.name,
                };
              }),
            );
          }

          return [];
        }),
      );

      setImages(formatted);
    };

    load();
  }, [
    cacheKey,
    images,
    isVisible,
    row.ID_MUESTRA,
    row.NumeroPresupuesto,
    row.ProductId,
  ]);

  if (images === null) {
    return (
      <Box
        ref={cellRef}
        sx={{
          width: 50,
          height: 50,
          background: "#ddd",
          borderRadius: 1,
        }}
      />
    );
  }

  return (
    <Box
      ref={cellRef}
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {images.slice(0, 1).map((img, idx) => (
        <Box
          key={idx}
          component="img"
          src={img.url}
          alt={img.fileName}
          onClick={(e) => {
            e.stopPropagation();
            onOpenImage(e.currentTarget, img.url);
          }}
          sx={{
            width: 50,
            height: 50,
            objectFit: "cover",
            borderRadius: 1,
            cursor: "pointer",
          }}
        />
      ))}
    </Box>
  );
});

const CriticalPathHorizontal1 = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { contentMargin } = useContext(SidebarContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accent = theme.palette.primary.main;

  const numberLocale = numberLocaleFromI18n(i18n.language);

  const getCloseEntityKey = (rowOrProductId, idMuestra = null, idPedido = null) => {
    if (rowOrProductId && typeof rowOrProductId === "object") {
      const row = rowOrProductId;
      if (
        row?.IdPresupuestoA3Pedido != null &&
        Number.isFinite(Number(row.IdPresupuestoA3Pedido))
      ) {
        return `D_${Number(row.IdPresupuestoA3Pedido)}`;
      }
      if (row?.ProductId != null && Number.isFinite(Number(row.ProductId))) {
        return `P_${Number(row.ProductId)}`;
      }
      if (row?.ID_MUESTRA != null && Number.isFinite(Number(row.ID_MUESTRA))) {
        return `M_${Number(row.ID_MUESTRA)}`;
      }
      return null;
    }
    if (idPedido != null && Number.isFinite(Number(idPedido))) {
      return `D_${Number(idPedido)}`;
    }
    if (
      rowOrProductId !== null &&
      rowOrProductId !== undefined &&
      Number.isFinite(Number(rowOrProductId))
    ) {
      return `P_${Number(rowOrProductId)}`;
    }
    if (idMuestra !== null && idMuestra !== undefined) {
      return `M_${Number(idMuestra)}`;
    }
    return null;
  };

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollX, setScrollX] = useState(0);
  const scrollRafRef = useRef(null);
  const scrollLatestRef = useRef(0);

  const [showFinished, setShowFinished] = useState(false);
  const [showClosedEntities, setShowClosedEntities] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedIdPresupuesto, setSelectedIdPresupuesto] = useState(null);
  const [selectedIdPresupuestoA3Pedido, setSelectedIdPresupuestoA3Pedido] =
    useState(null);

  const [subHeights, setSubHeights] = useState({});

  // Pintar SUbtareas en Tabla
  const [subTasksByProduct, setSubTasksByProduct] = useState({});

  const [refreshByProduct, setRefreshByProduct] = useState({});

  // Chat + Notas (unified drawer)
  const [cpChatState, setCpChatState] = useState({
    open: false,
    productId: null,
    idPresupuestoA3Pedido: null,
    title: "",
    subtitle: "",
    initialTab: "notas",
  });
  const [chatMetaMap, setChatMetaMap] = useState({});
  /*   const [notasProductId, setNotasProductId] = useState(null);
  const [notasMuestraId, setNotasMuestraId] = useState(null); */

  // Contar las notas para el badge
  const [notasCount, setNotasCount] = useState({});

  const loadChatSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { userId } = jwtDecode(token);
      const [prodSummary, pedidoSummary] = await Promise.all([
        getEntityChatSummary("Producto", userId),
        getEntityChatSummary("PedidoA3", userId),
      ]);
      const normalized = {};
      Object.entries(prodSummary || {}).forEach(([id, meta]) => {
        normalized[`Producto:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      Object.entries(pedidoSummary || {}).forEach(([id, meta]) => {
        normalized[`PedidoA3:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      setChatMetaMap(normalized);
    } catch (error) {
      console.error("Error loading CP chat summary:", error);
    }
  }, []);

  const clearChatUnreadForKey = useCallback((metaKey) => {
    if (!metaKey) return;
    setChatMetaMap((prev) => {
      const cur = prev[metaKey];
      if (!cur) return prev;
      const next = {
        ...prev,
        [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
      };
      const anyMention = Object.values(next).some((m) => m?.hasMention);
      if (!anyMention) setBrowserTabBadge(false);
      return next;
    });
  }, []);

  const openCpChatFromRow = useCallback(
    (row, tab = "mensajes") => {
      if (!row) return;
      const productId = row.ProductId ?? row.productId ?? null;
      const idPresupuestoA3Pedido = row.IdPresupuestoA3Pedido ?? null;
      const d = getCpDesignDisplay(row);
      setCpChatState({
        open: true,
        productId,
        idPresupuestoA3Pedido,
        title:
          d?.designCliente ||
          row.designCliente ||
          (idPresupuestoA3Pedido
            ? `Pedido ${idPresupuestoA3Pedido}`
            : `Product ${productId || ""}`),
        subtitle: d?.disenoMuestra || "",
        initialTab: tab,
      });
      clearChatUnreadForKey(
        getCpChatMetaKey({ productId, idPresupuestoA3Pedido }),
      );
    },
    [clearChatUnreadForKey],
  );

  const getRowChatMeta = useCallback(
    (row) => {
      const key = getCpChatMetaKeyFromRow(row);
      const meta = (key && chatMetaMap[key]) || {};
      return {
        total: Number(meta.total) || 0,
        noLeidos: Number(meta.noLeidos) || 0,
        hasMention: Boolean(meta.hasMention),
        hasMessages:
          Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0,
      };
    },
    [chatMetaMap],
  );

  const [sortField, setSortField] = useState("ETDF");
  const [sortDirection, setSortDirection] = useState("asc");
  const [renderedRowsCount, setRenderedRowsCount] =
    useState(INITIAL_RENDER_ROWS);
  const [closeConfirmState, setCloseConfirmState] = useState({
    open: false,
    row: null,
  });

  // ABRIR DRAWER MUESTRAS
  const [drawerMode, setDrawerMode] = useState(null); // PRODUCCION | MUESTRA
  const [selectedMuestraId, setSelectedMuestraId] = useState(null);
  const [selectedLegacyLogistics, setSelectedLegacyLogistics] = useState([]);
  const [selectedHasLegacyLogistics, setSelectedHasLegacyLogistics] =
    useState(false);
  const [selectedGridRowSnapshot, setSelectedGridRowSnapshot] = useState(null);

  // 🇨🇳 CHINA — usuario
  const { loading: proveedorLoading, filterByProveedor } =
    useProveedorPermissions();
  // 🇨🇳 CHINA — usuario

  useEffect(() => {
    const refreshCounts = async () => {
      try {
        const data = await getNotasCountAll();

        const counts = {};

        data.forEach((r) => {
          const key = getCpNotasCountKeyFromApiRow(r);
          if (key) counts[key] = Number(r.total);
        });

        setNotasCount(counts);
      } catch (e) {
        console.error("Error refreshing notas:", e);
      }
    };

    refreshCounts();
    socket.on("criticalpath-nota-count-updated", refreshCounts);

    return () => {
      socket.off("criticalpath-nota-count-updated", refreshCounts);
    };
  }, []);

  useEffect(() => {
    loadChatSummary();
  }, [loadChatSummary]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const currentUserId = token ? jwtDecode(token)?.userId : null;
    const openKey = cpChatState.open
      ? getCpChatMetaKey({
          productId: cpChatState.productId,
          idPresupuestoA3Pedido: cpChatState.idPresupuestoA3Pedido,
        })
      : null;

    const handleChatNuevo = ({
      entityType,
      entityId,
      mentionedUserIds,
      mensaje,
    }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      const openHere = Boolean(openKey && openKey === metaKey);
      const isMentioned =
        Number(mensaje?.AutorId) !== Number(currentUserId) &&
        (mentionedUserIds || []).map(Number).includes(Number(currentUserId));

      setChatMetaMap((prev) => {
        const cur = prev[metaKey] || {
          total: 0,
          noLeidos: 0,
          hasMention: false,
        };
        return {
          ...prev,
          [metaKey]: {
            total: (cur.total || 0) + 1,
            noLeidos: openHere ? 0 : (cur.noLeidos || 0) + 1,
            hasMessages: true,
            hasMention: openHere
              ? false
              : Boolean(cur.hasMention) || isMentioned,
          },
        };
      });

      if (isMentioned && !openHere) {
        setBrowserTabBadge(true);
      }
    };

    const handleChatLeidos = ({ entityType, entityId, userId: readBy }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      if (
        readBy != null &&
        currentUserId != null &&
        Number(readBy) !== Number(currentUserId)
      ) {
        return;
      }
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      setChatMetaMap((prev) => {
        const cur = prev[metaKey];
        if (!cur) return prev;
        return {
          ...prev,
          [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
        };
      });
      setBrowserTabBadge(false);
    };

    socket.on("chat-nuevo-mensaje", handleChatNuevo);
    socket.on("chat-mensajes-leidos", handleChatLeidos);
    return () => {
      socket.off("chat-nuevo-mensaje", handleChatNuevo);
      socket.off("chat-mensajes-leidos", handleChatLeidos);
    };
  }, [
    cpChatState.open,
    cpChatState.productId,
    cpChatState.idPresupuestoA3Pedido,
  ]);

  const ganttRef = useRef(null);

  // para el hover de imágenes
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);
  // Popover de la leyenda (colores + iconos)
  const [legendAnchor, setLegendAnchor] = useState(null);

  const legendStatusItems = useMemo(
    () => [
      {
        color: "#00c853",
        label: t("CriticalPathHorizontal.legendDonePlus10"),
      },
      { color: "#ff9800", label: t("CriticalPathHorizontal.legend6to10") },
      { color: "#e53935", label: t("CriticalPathHorizontal.legendLe5") },
      { color: "#9e9e9e", label: t("CriticalPathHorizontal.legendOverdue") },
    ],
    [t],
  );

  const legendIconItems = useMemo(
    () => [
      {
        icon: <ScienceOutlined sx={{ fontSize: 16, color: "#00C2A3" }} />,
        label: t("CriticalPathHorizontal.iconTesting"),
      },
      {
        icon: <FactoryOutlined sx={{ fontSize: 16, color: "#26A69A" }} />,
        label: t("CriticalPathHorizontal.iconProduction"),
      },
      {
        icon: <LocalShippingOutlined sx={{ fontSize: 16, color: "#42A5F5" }} />,
        label: t("CriticalPathHorizontal.iconShipmentBooking"),
      },
      {
        icon: <CheckCircleOutline sx={{ fontSize: 16, color: "#66BB6A" }} />,
        label: t("CriticalPathHorizontal.iconApproved"),
      },
      {
        icon: <DescriptionOutlined sx={{ fontSize: 16, color: "#8D6E63" }} />,
        label: t("CriticalPathHorizontal.iconDocuments"),
      },
      {
        icon: <EventOutlined sx={{ fontSize: 16, color: "#FFA726" }} />,
        label: t("CriticalPathHorizontal.iconDates"),
      },
      {
        icon: <ChatBubbleOutline sx={{ fontSize: 16, color: "#AB47BC" }} />,
        label: t("CriticalPathHorizontal.iconComments"),
      },
    ],
    [t],
  );

  dayjs.extend(minMax);

  // ✅ SOCKET.IO – Escucha cambios globales y recarga el Gantt
  useEffect(() => {
    if (proveedorLoading) return;
    let refreshTimer = null;

    const handleUpdate = async (data) => {
      if (!data?.productId && !data?.idMuestra) return;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      // Coalesce bursts of socket events into one fetch.
      refreshTimer = window.setTimeout(async () => {
        try {
          const raw = await loadFullCriticalPath();
          const flagged = withLegacyLogisticsFlag(raw);
          setRows(flagged);
        } catch (err) {
          console.error("❌ Error al refrescar por socket:", err);
        }
      }, 600);
    };

    /* socket.on("timelineUpdated", handleUpdate); */
    socket.on("timelineUpdated", (data) => {
      if (data?.productId) {
        handleUpdate(data);
      }
    });
    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off("timelineUpdated", handleUpdate);
    };
    // loadFullCriticalPath is stable for a given filterByProveedor; defined below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorLoading, filterByProveedor, showClosedEntities]);

  // 🧠 Cargar datos iniciales
  useEffect(() => {
    if (proveedorLoading) return;

    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    const loadData = async () => {
      try {
        setIsLoading(true);

        /* let data = await getCriticalPathHorizontal1(); */

        const [prod, closedRaw] = await Promise.all([
          getCriticalPathHorizontal1(true),
          getCriticalPathClosedEntities().catch(() => []),
        ]);

        const closedSet = new Set(
          (Array.isArray(closedRaw) ? closedRaw : [])
            .map((x) =>
              getCloseEntityKey(
                x?.ProductId,
                x?.IdMuestra,
                x?.IdPresupuestoA3Pedido,
              ),
            )
            .filter(Boolean),
        );

        let data = prodRowsExcludingMuestras(prod);
        data = data.map((r) => ({
          ...r,
          IsClosed: closedSet.has(getCloseEntityKey(r)),
        }));
        data = filterByProveedor(data);

        const flagged = withLegacyLogisticsFlag(data);
        setRows(flagged);
      } catch (err) {
        console.error("❌ Error cargando Critical Path:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, proveedorLoading, filterByProveedor, showClosedEntities]);

  /** Filas de producción: excluir ID_MUESTRA si el API las devolviera. */
  function prodRowsExcludingMuestras(prod) {
    return (Array.isArray(prod) ? prod : []).filter(
      (r) => r?.ID_MUESTRA == null,
    );
  }

  const loadFullCriticalPath = async () => {
    const [prod, closedRaw] = await Promise.all([
      getCriticalPathHorizontal1(true),
      getCriticalPathClosedEntities().catch(() => []),
    ]);

    const closedSet = new Set(
      (Array.isArray(closedRaw) ? closedRaw : [])
        .map((x) =>
          getCloseEntityKey(
            x?.ProductId,
            x?.IdMuestra,
            x?.IdPresupuestoA3Pedido,
          ),
        )
        .filter(Boolean),
    );

    let data = prodRowsExcludingMuestras(prod);
    data = data.map((r) => ({
      ...r,
      IsClosed: closedSet.has(getCloseEntityKey(r)),
    }));
    data = filterByProveedor(data);

    return data;
  };

  const handleToggleEntityClosed = async (row) => {
    try {
      const willClose = !Boolean(row?.IsClosed);
      await setCriticalPathEntityClosed({
        productId: row?.ProductId ?? null,
        idPresupuestoA3Pedido: row?.IdPresupuestoA3Pedido ?? null,
        idMuestra: row?.ID_MUESTRA ?? null,
        isClosed: willClose,
      });
      setRows((prev) =>
        prev
          .map((r) => (r.id === row.id ? { ...r, IsClosed: willClose } : r))
          .filter((r) =>
            showClosedEntities ? Boolean(r.IsClosed) : !Boolean(r.IsClosed),
          ),
      );
    } catch (error) {
      console.error("❌ Error cerrando/reabriendo fila CP:", error);
    }
  };

  const handleRequestToggleEntityClosed = (row) => {
    if (!row) return;
    const willClose = !Boolean(row?.IsClosed);
    if (!willClose) {
      handleToggleEntityClosed(row);
      return;
    }
    setCloseConfirmState({ open: true, row });
  };

  const handleConfirmCloseEntity = async () => {
    const rowToClose = closeConfirmState.row;
    setCloseConfirmState({ open: false, row: null });
    if (!rowToClose) return;
    await handleToggleEntityClosed(rowToClose);
  };

  const handleOpenRow = useCallback((r) => {
    if (!r?.id) return;

    if (r.ProductId) {
      setSelectedProductId(r.ProductId);
      setSelectedMuestraId(null);
      setSelectedIdPresupuestoA3Pedido(
        r.IdPresupuestoA3Pedido != null &&
          Number.isFinite(Number(r.IdPresupuestoA3Pedido))
          ? Number(r.IdPresupuestoA3Pedido)
          : null,
      );
      setSelectedIdPresupuesto(
        r.idPresupuestoCRM != null &&
          Number.isFinite(Number(r.idPresupuestoCRM))
          ? Number(r.idPresupuestoCRM)
          : parseIdPresupuestoFromRowId(r.id),
      );
      setSelectedLegacyLogistics(
        Array.isArray(r?.logistics)
          ? r.logistics.filter((l) => !Boolean(l?._fromLogisticaEnvio))
          : [],
      );
      setSelectedHasLegacyLogistics(Boolean(r?.HasLegacyLogistics));
      setSelectedGridRowSnapshot(r);
      setDrawerMode("PRODUCCION");
      setDrawerOpen(true);
      return;
    }

    if (r.ID_MUESTRA) {
      setSelectedProductId(null);
      setSelectedMuestraId(r.ID_MUESTRA);
      setSelectedIdPresupuesto(null);
      setSelectedIdPresupuestoA3Pedido(null);
      setSelectedLegacyLogistics([]);
      setSelectedHasLegacyLogistics(false);
      setSelectedGridRowSnapshot(null);
      setDrawerMode("MUESTRA");
      setDrawerOpen(true);
    }
  }, []);

  const isMobileRowActive = useCallback(
    (r) => {
      if (!drawerOpen) return false;
      if (drawerMode === "MUESTRA" && r.ID_MUESTRA) {
        return Number(r.ID_MUESTRA) === Number(selectedMuestraId);
      }
      if (drawerMode === "PRODUCCION" && r.ProductId) {
        const idPresupuestoRow =
          r.idPresupuestoCRM != null &&
          Number.isFinite(Number(r.idPresupuestoCRM))
            ? Number(r.idPresupuestoCRM)
            : parseIdPresupuestoFromRowId(r.id);
        const idPedidoRow =
          r.IdPresupuestoA3Pedido != null &&
          Number.isFinite(Number(r.IdPresupuestoA3Pedido))
            ? Number(r.IdPresupuestoA3Pedido)
            : null;
        return (
          r.ProductId === selectedProductId &&
          idPresupuestoRow === selectedIdPresupuesto &&
          idPedidoRow === selectedIdPresupuestoA3Pedido
        );
      }
      return false;
    },
    [
      drawerOpen,
      drawerMode,
      selectedMuestraId,
      selectedProductId,
      selectedIdPresupuesto,
      selectedIdPresupuestoA3Pedido,
    ],
  );

  const getExpectedDisplay = useCallback(
    (r) => {
      const expectedRaw = r.ID_MUESTRA
        ? r.FECHA_PREVISTA || r.FECHA_RECEPCION || null
        : r["ETDF"]?.ExpectedDate ||
          r["Fabrics and trims In-house"]?.ExpectedDate ||
          null;
      if (!expectedRaw || !dayjs(expectedRaw).isValid()) {
        return { text: "—", color: "text.secondary" };
      }
      const expected = dayjs(expectedRaw);
      const diffDays = expected.diff(dayjs(), "day");
      let color = "#00c853";
      let label = "";
      if (diffDays < 0) {
        color = "#9e9e9e";
        label = t("CriticalPathHorizontal.expectedOverdue", {
          days: Math.abs(diffDays),
        });
      } else if (diffDays === 0) {
        color = "#ff1744";
        label = t("CriticalPathHorizontal.expectedToday");
      } else if (diffDays <= 5) {
        color = "#e53935";
        label = t("CriticalPathHorizontal.expectedInDays", { days: diffDays });
      } else if (diffDays <= 10) {
        color = "#ff9800";
        label = t("CriticalPathHorizontal.expectedInDays", { days: diffDays });
      } else {
        label = t("CriticalPathHorizontal.expectedInDays", { days: diffDays });
      }
      return {
        text: `${expected.format("DD/MM/YYYY")} ${label}`.trim(),
        color,
      };
    },
    [t],
  );

  // 🔍 Filtrado dinámico
  useEffect(() => {
    let result = [...rows];

    result = result.filter((r) =>
      showClosedEntities ? Boolean(r?.IsClosed) : !Boolean(r?.IsClosed),
    );

    // 🔹 Filtro por texto
    if (deferredSearchTerm) {
      const lower = deferredSearchTerm.toLowerCase();
      result = result.filter((r) => {
        const fields = [
          r.CLiente,
          r.designCliente,
          r.ProveedorNombre,
          r.MarcaNombre,
          r.TemporadaNombre,
          r.PresupuestoEstado,
          r.NumeroPresupuesto,
          r.DiseñoMuestra,
          r.ReferenciaPedido,
          r.IdPedv,
          r.designCliente,
          r.Notas,
          r.FechaAceptacion,
          r.Units,
          r.UnitsPresupuesto,
          r["ETDF"]?.ExpectedDate,
          r["Fabrics and trims In-house"]?.ExpectedDate,
        ];
        return fields.some((f) =>
          String(f || "")
            .toLowerCase()
            .includes(lower),
        );
      });
    }

    setFilteredRows(result);
  }, [deferredSearchTerm, rows, showClosedEntities]);

  const filteredForFinished = React.useMemo(() => {
    let result = filteredRows;
    if (!showFinished) {
      result = result.filter((r) => {
        const logistics = r.logistics || [];
        const hasTelexDate = logistics.some(
          (entry) =>
            entry?.OriginalTelexDocsSemt &&
            dayjs(entry.OriginalTelexDocsSemt).isValid(),
        );
        return !hasTelexDate;
      });
    }
    return sortCriticalPathRows(result, sortField, sortDirection);
  }, [filteredRows, showFinished, sortField, sortDirection]);

  const ganttRange = useMemo(() => {
    const allDates = [];

    rows.forEach((r) => {
      // 🟣 MUESTRAS
      if (r.ID_MUESTRA && Array.isArray(r.TaskStatus)) {
        r.TaskStatus.forEach((t) => {
          [t.expected, t.updated, t.real, t.createdAt].forEach((d) => {
            const parsed = dayjs(d);
            if (parsed.isValid()) allDates.push(parsed.valueOf());
          });
        });
      }

      // 🟢 PRODUCCIÓN
      [
        r.FechaAceptacion,
        r["ETDF"]?.ExpectedDate,
        r["Fabrics and trims In-house"]?.ExpectedDate,
        r.FECHA_PREVISTA,
        r.createdAt,
      ].forEach((d) => {
        const parsed = dayjs(d);
        if (parsed.isValid()) allDates.push(parsed.valueOf());
      });
    });

    if (!allDates.length) {
      const now = Date.now();
      return { start: now - 7 * 86400000, end: now + 7 * 86400000 };
    }

    return {
      start: Math.min(...allDates),
      end: Math.max(...allDates),
    };
  }, [rows]);

  const startMin = ganttRange.start;
  const endMax = ganttRange.end;

  const dayWidth = 20;
  const rangeDays = Math.max(
    (endMax - startMin) / (1000 * 60 * 60 * 24),
    1,
  );
  const totalWidth = Math.max(rangeDays * dayWidth, 1200);
  const scaleX = (time) => {
    if (!Number.isFinite(endMax - startMin) || endMax === startMin) {
      return totalWidth / 2;
    }
    return ((time - startMin) / (endMax - startMin)) * totalWidth;
  };
  const today = useMemo(() => dayjs().startOf("day").valueOf(), []);
  const todayInRange = today >= startMin && today <= endMax;
  const todayX = todayInRange ? scaleX(today) : null;

  const scrollToToday = useCallback(() => {
    const ganttBody = ganttRef.current;
    if (!ganttBody || todayX == null) return;
    const centerX = Math.max(todayX - ganttBody.clientWidth / 2, 0);
    ganttBody.scrollLeft = centerX;
    setScrollX(centerX);
  }, [todayX]);

  const todayScrollKey = `${isLoading ? 1 : 0}:${showFinished ? 1 : 0}:${startMin}:${endMax}:${totalWidth}:${todayInRange ? 1 : 0}`;
  const lastTodayScrollKeyRef = useRef("");

  // Centrar en HOY al cargar o al cambiar el rango; no re-centrar si el usuario hace scroll.
  useEffect(() => {
    if (isLoading || !todayInRange) return;
    if (lastTodayScrollKeyRef.current === todayScrollKey) return;
    let cancelled = false;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled || !ganttRef.current || todayX == null) return;
        lastTodayScrollKeyRef.current = todayScrollKey;
        scrollToToday();
      });
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [todayScrollKey, isLoading, todayInRange, todayX, scrollToToday]);

  // 🧩 Forzar reflow vertical cuando cambian las alturas dinámicas
  useEffect(
    () => () => {
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const initialCount = Math.min(
      INITIAL_RENDER_ROWS,
      filteredForFinished.length || INITIAL_RENDER_ROWS,
    );
    setRenderedRowsCount(initialCount);

    const grow = () => {
      if (cancelled) return;

      setRenderedRowsCount((prev) => {
        const autoTarget = Math.min(
          filteredForFinished.length,
          MAX_AUTO_RENDER_ROWS,
        );
        if (prev >= autoTarget) return prev;
        timeoutId = window.setTimeout(grow, 60);
        return Math.min(prev + RENDER_CHUNK_ROWS, autoTarget);
      });
    };

    if (filteredForFinished.length > initialCount) {
      timeoutId = window.setTimeout(grow, 20);
    }

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [filteredForFinished.length]);

  const renderedRows = useMemo(
    () => filteredForFinished.slice(0, renderedRowsCount),
    [filteredForFinished, renderedRowsCount],
  );
  const hasMoreRows = renderedRowsCount < filteredForFinished.length;

  const ganttRowLayout = useMemo(() => {
    let y = 0;
    return renderedRows.map((r) => {
      const extra = expandedRowId === r.id ? subHeights[r.id] || 220 : 0;
      const top = y;
      y += ROW_HEIGHT + extra;
      return { top, extra };
    });
  }, [renderedRows, expandedRowId, subHeights]);

  const totalHeight = ganttRowLayout.reduce(
    (acc, layout) => acc + ROW_HEIGHT + layout.extra,
    0,
  );

  const filterFieldSx = pageFieldSx(theme);
  const pageShellSx = {
    display: "flex",
    flexDirection: "column",
    minHeight: { xs: "100dvh", md: "calc(100vh - 40px)" },
    boxSizing: "border-box",
    background:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.grey[50],
    pt: { xs: 1, md: 1.25 },
    px: { xs: 1.25, sm: 2 },
    pr: { xs: 1.25, sm: 2, md: 3 },
    pb: { xs: 10, md: 2 },
    minWidth: 0,
    overflow: "hidden",
    ...contentMargin,
  };
  const heroShellSx = {
    ...pageHeroShellSx(theme, { sticky: !isMobile }),
    mb: { xs: 1.25, md: 1 },
    flexShrink: 0,
    ...(theme.palette.mode === "light"
      ? {
          background: theme.palette.background.paper,
          borderColor: alpha(accent, 0.2),
          boxShadow: `0 10px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }
      : {}),
  };
  const filtersPanelSx = {
    ...pageContentCardSx(theme, accent, { highlighted: false }),
    p: { xs: 1.25, md: 1 },
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, minmax(0, 1fr))",
      md: "repeat(auto-fill, minmax(148px, 1fr))",
    },
    gap: 0.75,
    alignItems: "center",
  };
  const mobileDayWidth = 12;
  const mobileGanttWidth = Math.max(rangeDays * mobileDayWidth, 300);
  const mobileScaleX = (time) =>
    ((time - startMin) / (endMax - startMin)) * mobileGanttWidth;

  const renderMobileCpCard = (r) => {
    const d = getCpDesignDisplay(r);
    const clientName = r.CLiente || t("CriticalPathHorizontal.noClient");
    const clientAlias = r.MarcaNombre || "";
    const aliasIsDifferent =
      clientAlias &&
      clientAlias.trim().toUpperCase() !== clientName.trim().toUpperCase();
    const clientColor = getClientColor(clientName);
    const supplierName =
      r.ProveedorNombre || t("CriticalPathHorizontal.noSupplier");
    const supplierColor = getSupplierColor(supplierName);
    const expected = getExpectedDisplay(r);
    const entityKey =
      getCpNotasEntityKey({
        productId: r.ProductId,
        idMuestra: r.ID_MUESTRA,
        idPresupuestoA3Pedido: r.IdPresupuestoA3Pedido,
      }) || r.id;
    const expanded = expandedRowId === r.id;

    return (
      <Box
        key={r.id}
        sx={{
          ...pageMobileCardSx(theme, {
            active: isMobileRowActive(r),
            accentColor: accent,
          }),
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          onClick={() => handleOpenRow(r)}
          sx={{ cursor: "pointer" }}
        >
          <Box
            sx={{
              width: 72,
              minWidth: 72,
              height: 72,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              bgcolor: alpha(theme.palette.background.default, 0.55),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <PhotosCell
              row={r}
              onOpenImage={(anchor, url) => {
                setAnchorEl(anchor);
                setHoveredImage(url);
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: clientColor,
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {getEntityInitial(clientName)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {clientName}
                    {aliasIsDifferent ? ` · ${clientAlias}` : ""}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                    {d.designCliente || t("CriticalPathHorizontal.noDesign")}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
              >
                {(r.ProductId != null ||
                  r.ID_MUESTRA != null ||
                  r.IdPresupuestoA3Pedido != null) && (
                  <>
                    {(() => {
                      const { noLeidos, hasMessages, hasMention } =
                        getRowChatMeta(r);
                      return (
                        <CpChatDot
                          noLeidos={noLeidos}
                          hasMessages={hasMessages}
                          hasMention={hasMention}
                          onClick={() => openCpChatFromRow(r, "mensajes")}
                        />
                      );
                    })()}
                    <NotasBadge
                    key={`${entityKey}-${notasCount[entityKey] || 0}-mobile`}
                    productId={r.ProductId}
                    idMuestra={r.ID_MUESTRA}
                    idPresupuestoA3Pedido={r.IdPresupuestoA3Pedido}
                    notasCount={notasCount[entityKey] || 0}
                    onOpenDialog={() => openCpChatFromRow(r, "notas")}
                  />
                  </>
                )}
                <Tooltip
                  title={
                    r?.IsClosed
                      ? t("CriticalPathHorizontal.tooltipReopenCp")
                      : t("CriticalPathHorizontal.tooltipCloseCp")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestToggleEntityClosed(r);
                    }}
                    sx={{ color: r?.IsClosed ? "error.main" : "success.main" }}
                  >
                    {r?.IsClosed ? (
                      <VisibilityOutlined fontSize="small" />
                    ) : (
                      <VisibilityOffOutlined fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                {r?.HasLegacyLogistics ? (
                  <LegacyLogisticsIcon sx={{ fontSize: 18, color: "warning.main" }} />
                ) : null}
              </Stack>
            </Stack>

            <Typography variant="body2" color="orange" sx={{ fontSize: "0.75rem", mt: 0.35 }} noWrap>
              {d.disenoMuestra || "—"}
            </Typography>

            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.65 }}>
              <Chip
                size="small"
                label={supplierName}
                sx={{
                  ...pageKpiChipSx,
                  maxWidth: "100%",
                  bgcolor: alpha(supplierColor, 0.14),
                  color: supplierColor,
                  border: "1px solid",
                  borderColor: alpha(supplierColor, 0.24),
                }}
              />
              {r.TemporadaNombre ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={r.TemporadaNombre}
                  sx={{ ...pageKpiChipSx, maxWidth: "100%" }}
                />
              ) : null}
              {r.Units != null && r.Units !== "" ? (
                <Chip
                  size="small"
                  label={new Intl.NumberFormat(numberLocale, {
                    maximumFractionDigits: 0,
                  }).format(Number(r.Units))}
                  sx={pageKpiChipSx}
                />
              ) : null}
            </Stack>

            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.75, fontWeight: 700, color: expected.color }}
            >
              {expected.text}
            </Typography>
          </Box>
        </Stack>

        {r.ProductId ? (
          <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                setExpandedRowId(expanded ? null : r.id)
              }
              sx={{ fontWeight: 700, px: 0.5 }}
            >
              {expanded ? "Ocultar Gantt" : `Ver ${t("CriticalPathHorizontal.titleMiniGantt").replace(/^·\s*/, "")}`}
            </Button>
            {expanded ? (
              <Box
                sx={{
                  mt: 0.5,
                  overflowX: "auto",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                }}
              >
                <Box sx={{ width: mobileGanttWidth, minHeight: subHeights[r.id] || 120 }}>
                  <CriticalPathInlineGanttAligned
                    productId={r.ProductId}
                    idPresupuesto={
                      r.idPresupuestoCRM != null &&
                      Number.isFinite(Number(r.idPresupuestoCRM))
                        ? Number(r.idPresupuestoCRM)
                        : null
                    }
                    idPresupuestoA3Pedido={
                      r.IdPresupuestoA3Pedido != null &&
                      Number.isFinite(Number(r.IdPresupuestoA3Pedido))
                        ? Number(r.IdPresupuestoA3Pedido)
                        : null
                    }
                    hideLegacyTimeline={Boolean(
                      r?.HasLegacyLogistics &&
                        r?.IdPresupuestoA3Pedido != null &&
                        Number.isFinite(Number(r.IdPresupuestoA3Pedido)),
                    )}
                    preferLegacyTimeline={Boolean(
                      r?.HasLegacyLogistics &&
                        (r?.IdPresupuestoA3Pedido == null ||
                          !Number.isFinite(Number(r.IdPresupuestoA3Pedido))),
                    )}
                    gridRowSnapshot={r}
                    clientName={r.CLiente}
                    scaleX={mobileScaleX}
                    startMin={startMin}
                    endMax={endMax}
                    refreshKey={refreshByProduct[r.id] || 0}
                    onHeightChange={(h) =>
                      setSubHeights((prev) => ({ ...prev, [r.id]: h }))
                    }
                    onTimelineLoaded={(tasks) =>
                      setSubTasksByProduct((prev) => ({
                        ...prev,
                        [r.id]: tasks,
                      }))
                    }
                  />
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  if (isLoading)
    return (
      <Box
        sx={{
          ...pageShellSx,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  const headerCellSx = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <Box sx={pageShellSx}>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 1,
        }}
      >
        <Box sx={heroShellSx}>
          <Stack spacing={{ xs: 1.25, md: 0.875 }} sx={{ p: { xs: 1.25, md: 1, lg: 1.25 } }}>
            <Stack direction="row" spacing={{ xs: 1.25, md: 1 }} alignItems="flex-start">
              <Box
                sx={{
                  width: { xs: 44, md: 36 },
                  height: { xs: 44, md: 36 },
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: alpha(accent, 0.12),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.24),
                }}
              >
                <RouteOutlinedIcon sx={{ fontSize: { xs: "1.25rem", md: "1.1rem" } }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ ...pageSectionTitleSx, fontSize: { xs: "0.68rem", md: "0.62rem" } }}>
                  {t("CriticalPathHorizontal.chipCrm")}
                </Typography>
                <Typography
                  variant={isMobile ? "h5" : "h6"}
                  component="h1"
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  {t("CriticalPathHorizontal.title")}{" "}
                  <Box component="span" sx={{ color: theme.palette.primary.main }}>
                    {t("CriticalPathHorizontal.titleMiniGantt")}
                  </Box>
                </Typography>
                {!!filteredForFinished.length ? (
                  <Chip
                    size="small"
                    label={`${renderedRowsCount} / ${filteredForFinished.length}`}
                    sx={{
                      ...pageKpiChipSx,
                      mt: 0.5,
                      bgcolor: alpha(accent, 0.12),
                      color: "primary.dark",
                      border: "1px solid",
                      borderColor: alpha(accent, 0.2),
                    }}
                  />
                ) : null}
              </Box>
            </Stack>

            {isMobile ? (
              <TextField
                size="small"
                placeholder={t("CriticalPathHorizontal.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={filterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchTerm("")} size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            ) : null}

            <Box sx={filtersPanelSx}>
              {!isMobile ? (
                <TextField
                  size="small"
                  placeholder={t("CriticalPathHorizontal.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    ...filterFieldSx,
                    gridColumn: { md: "span 2" },
                    maxWidth: { md: 340 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchTerm("")} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              ) : null}
              <TextField
                select
                size="small"
                label={t("CriticalPathHorizontal.labelSortBy")}
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                sx={filterFieldSx}
              >
                {CP_SORT_FIELD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.labelKey
                      ? t(`CriticalPathHorizontal.${opt.labelKey}`)
                      : opt.id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={t("CriticalPathHorizontal.labelSortDirection")}
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value)}
                sx={filterFieldSx}
              >
                <MenuItem value="asc">{t("CriticalPathHorizontal.sortAsc")}</MenuItem>
                <MenuItem value="desc">{t("CriticalPathHorizontal.sortDesc")}</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showFinished}
                    onChange={(e) => setShowFinished(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("CriticalPathHorizontal.labelFinished")}
                  </Typography>
                }
                sx={{ m: 0, gridColumn: { xs: "1 / -1", sm: "auto" }, alignSelf: "center" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showClosedEntities}
                    onChange={(e) => setShowClosedEntities(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("CriticalPathHorizontal.labelClosedCp")}
                  </Typography>
                }
                sx={{ m: 0, gridColumn: { xs: "1 / -1", sm: "auto" }, alignSelf: "center" }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<InfoOutlinedIcon />}
                onClick={(e) => setLegendAnchor(e.currentTarget)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                }}
              >
                {t("CriticalPathHorizontal.legend")}
              </Button>
              {hasMoreRows && !isMobile ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() =>
                    setRenderedRowsCount((prev) =>
                      Math.min(prev + RENDER_CHUNK_ROWS, filteredForFinished.length),
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    gridColumn: { xs: "1 / -1", sm: "auto" },
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  {t("CriticalPathHorizontal.loadMore30")}
                </Button>
              ) : null}
            </Box>
          </Stack>
        </Box>

        {/* 🧭 Popover con leyendas (colores + iconos) */}
        <Popover
          open={Boolean(legendAnchor)}
          anchorEl={legendAnchor}
          onClose={() => setLegendAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 2,
              mt: 0.5,
              borderRadius: 2,
              minWidth: 320,
              maxWidth: 420,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.5 : 0.12)}`,
            },
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  display: "block",
                  mb: 0.75,
                }}
              >
                {t("CriticalPathHorizontal.legendStatusTitle")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {legendStatusItems.map((item) => (
                  <Box
                    key={`status-${item.color}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 0.75,
                      py: 0.4,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: alpha(item.color, 0.4),
                      backgroundColor: alpha(item.color, 0.08),
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        border: "1px solid #4442",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  display: "block",
                  mb: 0.75,
                }}
              >
                {t("CriticalPathHorizontal.legendGanttLinesTitle")}
              </Typography>
              <Stack spacing={0.6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 0,
                      borderTop: `4px solid ${EXPECTED_LINE_COLOR}`,
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                  >
                    {t("CriticalPathHorizontal.legendGanttExpected")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 0,
                      borderTop: `6px solid ${theme.palette.primary.main}`,
                      borderRadius: 1,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                  >
                    {t("CriticalPathHorizontal.legendGanttActual")}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  display: "block",
                  mb: 0.75,
                }}
              >
                {t("CriticalPathHorizontal.legendIconsTitle")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 0.75,
                }}
              >
                {legendIconItems.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                  >
                    {item.icon}
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </Popover>
        {isMobile ? (
          <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 0.25 }}>
            {!filteredRows.length ? (
              <Typography sx={{ p: 2 }}>
                {t("CriticalPathHorizontal.emptyData")}
              </Typography>
            ) : (
              <>
                {renderedRows.map((r) => renderMobileCpCard(r))}
                {hasMoreRows ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setRenderedRowsCount((prev) =>
                        Math.min(
                          prev + RENDER_CHUNK_ROWS,
                          filteredForFinished.length,
                        ),
                      )
                    }
                    sx={{ alignSelf: "center", fontWeight: 700 }}
                  >
                    {t("CriticalPathHorizontal.loadMore30")}
                  </Button>
                ) : null}
              </>
            )}
          </Stack>
        ) : (
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pr: 0.5 }}>
          {!filteredRows.length && (
            <Typography sx={{ p: 3 }}>
              {t("CriticalPathHorizontal.emptyData")}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {/* 📋 Tabla lateral */}
            <Box
              sx={{
                width: "51em",
                flexShrink: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 4px 16px rgba(0,0,0,0.32)"
                    : "0 4px 16px rgba(15,23,42,0.05)",
              }}
            >
              {/* Cabecera de la tabla */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: theme.palette.estimates.main,
                  backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.1 : 0.05)} 0%, ${alpha(theme.palette.estimates.main, 0)} 100%)`,
                  display: "flex",
                  p: 1,
                  pt: 3,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  height: HEADER_HEIGHT,
                  zIndex: 10,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Spacer alineado con el slot de iconos del cuerpo */}
                <Box sx={{ width: 86, flexShrink: 0 }} />

                <Tooltip
                  title={t("CriticalPathHorizontal.tooltipClientHeader")}
                >
                  <Box
                    sx={{
                      width: 80,
                      flexShrink: 0,
                      ...headerCellSx,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <PersonOutlined
                      sx={{
                        fontSize: 14,
                        color: theme.palette.text.secondary,
                      }}
                    />
                    {t("CriticalPathHorizontal.headerClient")}
                  </Box>
                </Tooltip>

                <Box
                  title={t("CriticalPathHorizontal.designColumnTitle")}
                  sx={{
                    width: 132,
                    flexShrink: 0,
                    ...headerCellSx,
                  }}
                >
                  {t("CriticalPathHorizontal.headerDesign")}
                </Box>

                <Tooltip
                  title={t("CriticalPathHorizontal.tooltipSupplierHeader")}
                >
                  <Box
                    sx={{
                      width: 75,
                      flexShrink: 0,
                      ...headerCellSx,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <FactoryOutlined
                      sx={{
                        fontSize: 14,
                        color: theme.palette.text.secondary,
                      }}
                    />
                    {t("CriticalPathHorizontal.headerSupplier")}
                  </Box>
                </Tooltip>

                <Tooltip title={t("CriticalPathHorizontal.tooltipUnitsHeader")}>
                  <Box
                    sx={{
                      width: 64,
                      flexShrink: 0,
                      ...headerCellSx,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {t("CriticalPathHorizontal.headerUnitsAbbr")}
                  </Box>
                </Tooltip>

                <Box
                  sx={{
                    width: 100,
                    flexShrink: 0,
                    ...headerCellSx,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {t("CriticalPathHorizontal.headerPhotos")}
                </Box>

                <Box
                  sx={{
                    width: 95,
                    flexShrink: 0,
                    ...headerCellSx,
                  }}
                >
                  {t("CriticalPathHorizontal.headerAccepted")}
                </Box>

                <Box
                  sx={{
                    width: 160,
                    flexShrink: 0,
                    ...headerCellSx,
                  }}
                >
                  {t("CriticalPathHorizontal.headerExpected")}
                </Box>
              </Box>

              {/* Filas */}

              {renderedRows.map((r, i) => {
                const entityKey =
                  getCpNotasEntityKey({
                    productId: r.ProductId,
                    idMuestra: r.ID_MUESTRA,
                    idPresupuestoA3Pedido: r.IdPresupuestoA3Pedido,
                  }) || r.id;
                return (
                  <React.Fragment key={r.id || i}>
                    {/* Fila principal */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        backgroundColor:
                          i % 2 === 0
                            ? theme.palette.estimates.main
                            : theme.palette.background.paper,
                        px: 1,
                        height:
                          ROW_HEIGHT +
                          (expandedRowId === r.id
                            ? subHeights[r.id] || 220
                            : 0),
                        transition:
                          "height 0.35s ease, background-color 0.15s ease",
                        overflow: "hidden",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.1 : 0.05,
                          ),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          if (!r.id) return;

                          if (r.ProductId) {
                            setSelectedProductId(r.ProductId);
                            setSelectedMuestraId(null);
                            setSelectedIdPresupuestoA3Pedido(
                              r.IdPresupuestoA3Pedido != null &&
                                Number.isFinite(Number(r.IdPresupuestoA3Pedido))
                                ? Number(r.IdPresupuestoA3Pedido)
                                : null,
                            );
                            setSelectedIdPresupuesto(
                              r.idPresupuestoCRM != null &&
                                Number.isFinite(Number(r.idPresupuestoCRM))
                                ? Number(r.idPresupuestoCRM)
                                : parseIdPresupuestoFromRowId(r.id),
                            );
                            setSelectedLegacyLogistics(
                              Array.isArray(r?.logistics)
                                ? r.logistics.filter(
                                    (l) => !Boolean(l?._fromLogisticaEnvio),
                                  )
                                : [],
                            );
                            setSelectedHasLegacyLogistics(
                              Boolean(r?.HasLegacyLogistics),
                            );
                            setSelectedGridRowSnapshot(r);
                            setDrawerMode("PRODUCCION");
                            setDrawerOpen(true);
                            return;
                          }

                          if (r.ID_MUESTRA) {
                            setSelectedProductId(null);
                            setSelectedMuestraId(r.ID_MUESTRA);
                            setSelectedIdPresupuesto(null);
                            setSelectedIdPresupuestoA3Pedido(null);
                            setSelectedLegacyLogistics([]);
                            setSelectedHasLegacyLogistics(false);
                            setSelectedGridRowSnapshot(null);
                            setDrawerMode("MUESTRA");
                            setDrawerOpen(true);
                          }
                        }}
                      >
                        {/* Slot fijo para iconos de estado (notas + visibilidad + legacy)
                          → todas las filas tienen el mismo offset, no se desplazan Client / Units */}
                        <Box
                          sx={{
                            width: 86,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            flexShrink: 0,
                            gap: 0.25,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              gap: 0.25,
                            }}
                          >
                            {(r.ProductId != null ||
                              r.ID_MUESTRA != null ||
                              r.IdPresupuestoA3Pedido != null) && (
                              <>
                                {(() => {
                                  const { noLeidos, hasMessages, hasMention } =
                                    getRowChatMeta(r);
                                  return (
                                    <CpChatDot
                                      noLeidos={noLeidos}
                                      hasMessages={hasMessages}
                                      hasMention={hasMention}
                                      onClick={() =>
                                        openCpChatFromRow(r, "mensajes")
                                      }
                                    />
                                  );
                                })()}
                                <NotasBadge
                                key={`${entityKey}-${notasCount[entityKey] || 0}`}
                                productId={r.ProductId}
                                idMuestra={r.ID_MUESTRA}
                                idPresupuestoA3Pedido={r.IdPresupuestoA3Pedido}
                                notasCount={notasCount[entityKey] || 0}
                                onOpenDialog={() => openCpChatFromRow(r, "notas")}
                              />
                              </>
                            )}
                          </Box>
                          <Box
                            sx={{
                              width: 26,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Tooltip
                              title={
                                r?.IsClosed
                                  ? t("CriticalPathHorizontal.tooltipReopenCp")
                                  : t("CriticalPathHorizontal.tooltipCloseCp")
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRequestToggleEntityClosed(r);
                                }}
                                sx={{
                                  p: 0.4,
                                  color: r?.IsClosed
                                    ? "error.main"
                                    : "success.main",
                                }}
                              >
                                {r?.IsClosed ? (
                                  <VisibilityOutlined fontSize="inherit" />
                                ) : (
                                  <VisibilityOffOutlined fontSize="inherit" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box
                            sx={{
                              width: 22,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {r?.HasLegacyLogistics ? (
                              <Tooltip
                                title={t(
                                  "CriticalPathHorizontal.tooltipLegacyLogistics",
                                )}
                              >
                                <LegacyLogisticsIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "warning.main",
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>

                        {(() => {
                          const clientName =
                            r.CLiente || t("CriticalPathHorizontal.noClient");
                          const clientAlias = r.MarcaNombre || "";
                          const aliasIsDifferent =
                            clientAlias &&
                            clientAlias.trim().toUpperCase() !==
                              clientName.trim().toUpperCase();
                          const clientColor = getClientColor(clientName);
                          const clientTooltip = (
                            <>
                              <strong>{clientName}</strong>
                              {aliasIsDifferent ? (
                                <>
                                  <br />
                                  <em>
                                    {t("CriticalPathHorizontal.aliasPrefix")}{" "}
                                    {clientAlias}
                                  </em>
                                </>
                              ) : null}
                            </>
                          );
                          return (
                            <Tooltip
                              title={clientTooltip}
                              arrow
                              placement="top"
                            >
                              <Box
                                sx={{
                                  width: 80,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 800,
                                    fontSize: "0.72rem",
                                    letterSpacing: 0.2,
                                    color: "#fff",
                                    backgroundColor: clientColor,
                                    boxShadow: `0 2px 6px ${alpha(
                                      clientColor,
                                      0.35,
                                    )}`,
                                    border: `1px solid ${alpha("#000", 0.08)}`,
                                    cursor: "default",
                                    userSelect: "none",
                                    flexShrink: 0,
                                  }}
                                >
                                  {getEntityInitial(clientName)}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })()}

                        <Tooltip
                          title={
                            (() => {
                              const d = getCpDesignDisplay(r);
                              return (
                                <>
                                  <strong>
                                    {d.designCliente ||
                                      t("CriticalPathHorizontal.noClient")}
                                  </strong>
                                  <br />
                                  {d.disenoMuestra ||
                                    t("CriticalPathHorizontal.noDesign")}
                                  {d.referenciaPedido ? (
                                    <>
                                      <br />
                                      Pedido: {d.referenciaPedido}
                                      {d.idPedv ? ` (A3 #${d.idPedv})` : ""}
                                    </>
                                  ) : null}
                                </>
                              );
                            })()
                          }
                        >
                          <Box
                            sx={{
                              width: 132,
                              minWidth: 132,
                              maxWidth: 132,
                              flexShrink: 0,
                              overflow: "hidden",
                              pr: 1,
                            }}
                          >
                            {(() => {
                              const d = getCpDesignDisplay(r);
                              return (
                                <>
                                  <Typography
                                    variant="caption"
                                    color="#00c2a3"
                                    sx={{
                                      display: "block",
                                      fontSize: "0.72rem",
                                      fontWeight: 900,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {d.designCliente}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="orange"
                                    sx={{
                                      fontSize: "0.7rem",
                                      display: "block",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {d.disenoMuestra}
                                  </Typography>
                                  {d.referenciaPedido ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        fontSize: "0.62rem",
                                        fontWeight: 700,
                                        color: "#7c4dff",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {d.referenciaPedido}
                                    </Typography>
                                  ) : null}
                                </>
                              );
                            })()}
                          </Box>
                        </Tooltip>

                        {(() => {
                          const primary = getSupplierColor(r.ProveedorNombre);
                          const tooltipTitle = (
                            <strong>
                              {r.ProveedorNombre ||
                                t("CriticalPathHorizontal.noSupplier")}
                            </strong>
                          );
                          return (
                            <Tooltip title={tooltipTitle} arrow placement="top">
                              <Box
                                sx={{
                                  width: 75,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.6,
                                  flexShrink: 0,
                                }}
                              >
                                {/* Avatar sólido (color por proveedor) */}
                                <Box
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: "50%",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 800,
                                    fontSize: "0.72rem",
                                    letterSpacing: 0.2,
                                    color: "#fff",
                                    backgroundColor: primary,
                                    boxShadow: `0 2px 6px ${alpha(
                                      primary,
                                      0.35,
                                    )}`,
                                    border: `1px solid ${alpha("#000", 0.08)}`,
                                    cursor: "default",
                                    userSelect: "none",
                                    flexShrink: 0,
                                  }}
                                >
                                  {getEntityInitial(r.ProveedorNombre)}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })()}

                        <Tooltip
                          title={
                            r.ID_MUESTRA
                              ? t("CriticalPathHorizontal.unitsTooltipSamples")
                              : r.UnitsPresupuesto != null &&
                                  r.Units != null &&
                                  Number(r.UnitsPresupuesto) !== Number(r.Units)
                                ? t(
                                    "CriticalPathHorizontal.unitsTooltipDisplayed",
                                    {
                                      shown: Number(r.Units).toLocaleString(
                                        numberLocale,
                                      ),
                                      budget: Number(
                                        r.UnitsPresupuesto,
                                      ).toLocaleString(numberLocale),
                                    },
                                  )
                                : t(
                                    "CriticalPathHorizontal.unitsTooltipDefault",
                                  )
                          }
                        >
                          <Box
                            sx={{
                              width: 64,
                              fontSize: ".7rem",
                              fontWeight: 700,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {r.ID_MUESTRA
                              ? "—"
                              : r.Units != null &&
                                  Number.isFinite(Number(r.Units))
                                ? new Intl.NumberFormat(numberLocale, {
                                    maximumFractionDigits: 0,
                                  }).format(Number(r.Units))
                                : "—"}
                          </Box>
                        </Tooltip>

                        <Box
                          sx={{
                            width: 100,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PhotosCell
                            row={r}
                            onOpenImage={(anchor, url) => {
                              setAnchorEl(anchor);
                              setHoveredImage(url);
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            width: 95,
                            flexShrink: 0,
                            fontSize: ".7rem",
                          }}
                        >
                          {r.FechaAceptacion
                            ? dayjs(r.FechaAceptacion).format("DD/MM/YYYY")
                            : "—"}
                        </Box>

                        <Box
                          sx={{ width: 160, flexShrink: 0, fontSize: ".7rem" }}
                        >
                          {(() => {
                            const expectedRaw = r.ID_MUESTRA
                              ? r.FECHA_PREVISTA || r.FECHA_RECEPCION || null
                              : r["ETDF"]?.ExpectedDate ||
                                r["Fabrics and trims In-house"]?.ExpectedDate ||
                                null;
                            if (!expectedRaw || !dayjs(expectedRaw).isValid())
                              return "—";
                            const expected = dayjs(expectedRaw);
                            const diffDays = expected.diff(dayjs(), "day");
                            let color = "#00c853";
                            let label = "";
                            if (diffDays < 0) {
                              color = "#9e9e9e";
                              label = t(
                                "CriticalPathHorizontal.expectedOverdue",
                                { days: Math.abs(diffDays) },
                              );
                            } else if (diffDays === 0) {
                              color = "#ff1744";
                              label = t("CriticalPathHorizontal.expectedToday");
                            } else if (diffDays <= 5) {
                              color = "#e53935";
                              label = t(
                                "CriticalPathHorizontal.expectedInDays",
                                { days: diffDays },
                              );
                            } else if (diffDays <= 10) {
                              color = "#ff9800";
                              label = t(
                                "CriticalPathHorizontal.expectedInDays",
                                { days: diffDays },
                              );
                            } else {
                              label = t(
                                "CriticalPathHorizontal.expectedInDays",
                                { days: diffDays },
                              );
                            }
                            return (
                              <Typography
                                component="span"
                                sx={{
                                  color,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {expected.format("DD/MM/YYYY")} {label}
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Box>

                      <Box>
                        {expandedRowId === r.id && subTasksByProduct[r.id] && (
                          <Box
                            sx={{
                              pr: 4,
                              pt: 1,
                              mt: 0.8,
                              mb: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.68,
                              alignItems: "flex-end",
                              backgroundColor: "#f3f7f7",
                              borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {subTasksByProduct[r.id].map((t, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "#555",
                                  pl: 1,
                                }}
                              >
                                {t.TaskName}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            {/* 📊 Gantt principal */}
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: theme.palette.estimates.main,
                display: "block", // ✅ quita flex para permitir crecimiento vertical real
                position: "relative",
                overflow: "hidden",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 4px 16px rgba(0,0,0,0.32)"
                    : "0 4px 16px rgba(15,23,42,0.05)",
              }}
            >
              {/* Línea HOY única (cabecera + cuerpo), anclada al scroll del cuerpo */}
              {todayInRange && todayX != null ? (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: todayX - scrollX,
                    width: 0,
                    borderLeft: "2px dashed #e53935",
                    zIndex: 12,
                    pointerEvents: "none",
                  }}
                />
              ) : null}
              {/* 🔝 Cabecera sticky */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 9,
                  backgroundColor: theme.palette.estimates.main,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  backdropFilter: "blur(4px)",
                }}
              >
                <Box
                  id="gantt-header-scroll"
                  sx={{
                    overflow: "hidden",
                    width: "100%",
                    position: "relative",
                  }}
                >
                  <svg
                    width={totalWidth}
                    height={HEADER_HEIGHT}
                    style={{
                      display: "block",
                      backgroundColor: theme.palette.estimates.main,
                      transform: `translateX(-${scrollX}px)`,
                      willChange: "transform",
                    }}
                  >
                    <rect
                      x="0"
                      y="0"
                      width={totalWidth}
                      height={HEADER_HEIGHT}
                      fill={theme.palette.estimates.main}
                    />

                    {/* 🧭 Fechas exactas cada 4 días, empezando en startMin */}
                    {Array.from({
                      length: Math.ceil(rangeDays / 4) + 1,
                    }).map((_, i) => {
                      const date = dayjs(startMin).add(i * 4, "day");
                      const x = scaleX(date.valueOf());
                      return (
                        <text
                          key={i}
                          x={x}
                          y={HEADER_HEIGHT - 20}
                          fontSize="10"
                          textAnchor="middle"
                          fill={theme.palette.text.primary}
                        >
                          {date.format("DD/MM")}
                        </text>
                      );
                    })}

                    {/* Label HOY (la línea vertical la pinta el overlay del contenedor) */}
                    {todayInRange && todayX != null && (
                      <>
                        <rect
                          x={todayX - 14}
                          y={4}
                          width="40"
                          height="16"
                          fill={theme.palette.estimates.main}
                          rx="3"
                        />
                        <text
                          x={todayX + 5}
                          y={16}
                          fill="#e53935"
                          fontWeight="bold"
                          fontSize="11"
                        >
                          {t("CriticalPathHorizontal.ganttToday")}
                        </text>
                      </>
                    )}
                  </svg>
                </Box>
              </Box>

              {/* 🔽 Cuerpo scrollable */}
              <Box
                id="gantt-body-scroll"
                ref={ganttRef}
                sx={{
                  overflowX: "auto",
                  overflowY: "visible", // ✅ deja expandir hacia abajo
                  width: "100%",
                  cursor: "grab",
                  display: "block",
                }}
                onScroll={(e) => {
                  const nextLeft = e.target.scrollLeft;
                  scrollLatestRef.current = nextLeft;
                  if (scrollRafRef.current) return;
                  scrollRafRef.current = window.requestAnimationFrame(() => {
                    setScrollX(scrollLatestRef.current);
                    scrollRafRef.current = null;
                  });
                }}
              >
                <svg
                  width={totalWidth}
                  height={totalHeight}
                  style={{
                    display: "block",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {/* Fondos alternos (altura real, incluida la fila expandida) */}
                  {renderedRows.map((r, i) => {
                    const layout = ganttRowLayout[i] || { top: 0, extra: 0 };
                    const isFinished =
                      r.logistics?.some(
                        (entry) =>
                          entry?.OriginalTelexDocsSemt &&
                          dayjs(entry.OriginalTelexDocsSemt).isValid(),
                      ) || false;
                    const fillColor = isFinished
                      ? "#f3ffebff"
                      : i % 2 === 0
                        ? theme.palette.estimates.main
                        : theme.palette.background.paper;

                    return (
                      <rect
                        key={`bg-${i}`}
                        x="0"
                        y={layout.top}
                        width={totalWidth}
                        height={ROW_HEIGHT + layout.extra}
                        fill={fillColor}
                      />
                    );
                  })}

                  {/* Líneas verticales cada 4 días */}
                  {Array.from({ length: Math.ceil(rangeDays / 4) + 1 }).map(
                    (_, i) => {
                      const date = dayjs(startMin).add(i * 4, "day");
                      const x = scaleX(date.valueOf());
                      return (
                        <line
                          key={`grid-${i}`}
                          x1={x}
                          x2={x}
                          y1={0}
                          y2={totalHeight}
                          stroke={theme.palette.divider}
                          strokeWidth="0.8"
                        />
                      );
                    },
                  )}

                  {/* Barras + dots + subGantt */}
                  {renderedRows.map((r, i) => {
                      const layout = ganttRowLayout[i] || { top: 0, extra: 0 };
                      const currentY = layout.top;

                      const { expected, actual } = collectGanttBarDates(r);
                      const expectedExtent = dateExtent(expected);
                      const actualExtent = dateExtent(actual);
                      const expectedLine = extentToLineXs(
                        expectedExtent,
                        scaleX,
                        startMin,
                        endMax,
                      );
                      const actualLine = extentToLineXs(
                        actualExtent,
                        scaleX,
                        startMin,
                        endMax,
                      );

                      const yMid = currentY + ROW_HEIGHT / 2;
                      const hasBothLines = Boolean(expectedLine && actualLine);
                      const yExpected = hasBothLines
                        ? yMid - GANTT_LINE_OFFSET
                        : yMid;
                      const yActual = hasBothLines
                        ? yMid + GANTT_LINE_OFFSET
                        : yMid;

                      const color = getClientColor(r.CLiente);

                      const isFinished =
                        r.logistics?.some(
                          (entry) =>
                            entry?.OriginalTelexDocsSemt &&
                            dayjs(entry.OriginalTelexDocsSemt).isValid(),
                        ) || false;

                      const toggleExpanded = () =>
                        setExpandedRowId(
                          expandedRowId === r.id ? null : r.id,
                        );

                      return (
                        <g key={r.id || i}>
                          {/* bolitas intermedias */}
                          {r.TaskStatus?.map((t, j) => {
                            const expectedDate = parseTaskDate(t.expected);
                            const actualDate =
                              parseTaskDate(t.real) || parseTaskDate(t.updated);
                            const dateToUse =
                              actualDate ||
                              expectedDate ||
                              parseTaskDate(t.createdAt);

                            const dotTime = dateToUse
                              ? dateToUse.valueOf()
                              : null;

                            if (!dotTime) return null;

                            const clampedTime = Math.min(
                              Math.max(dotTime, startMin),
                              endMax,
                            );

                            const dotX = scaleX(clampedTime);
                            const iconY = actualDate ? yActual : yExpected;

                            const exp = expectedDate;
                            const real = parseTaskDate(t.real);
                            let dotColor = "#00c853";

                            if (real) {
                              dotColor = "#00c853";
                            } else if (exp) {
                              const diffDays = exp.diff(dayjs(), "day");
                              if (diffDays < 0)
                                dotColor = "#9e9e9e";
                              else if (diffDays === 0)
                                dotColor = "#ff1744";
                              else if (diffDays <= 5)
                                dotColor = "#e53935";
                              else if (diffDays <= 10)
                                dotColor = "#ff9800";
                              else dotColor = "#00c853";
                            }

                            const IconComp =
                              ICON_MAP[t.name] || RadioButtonUnchecked;

                            return (
                              <g key={`${r.id}-dot-${j}`}>
                                {dateToUse && (
                                  <line
                                    x1={dotX}
                                    x2={dotX}
                                    y1={Math.min(yExpected, yActual) - 12}
                                    y2={Math.max(yExpected, yActual) + 6}
                                    stroke="#ccc"
                                    strokeWidth="0.6"
                                    strokeDasharray="2 2"
                                  />
                                )}

                                <Tooltip
                                  title={
                                    <>
                                      <Typography fontWeight={700}>
                                        {t.name}
                                      </Typography>
                                      <Typography variant="caption">
                                        Exp: {t.expected || "-"}
                                      </Typography>
                                      <br />
                                      <Typography variant="caption">
                                        Upd: {t.updated || "-"}
                                      </Typography>
                                      <br />
                                      <Typography variant="caption">
                                        Real: {t.real || "-"}
                                      </Typography>
                                    </>
                                  }
                                  arrow
                                >
                                  <g style={{ cursor: "pointer" }}>
                                    <foreignObject
                                      x={dotX - 8}
                                      y={iconY - 18}
                                      width={18}
                                      height={18}
                                    >
                                      <IconComp
                                        sx={{
                                          fontSize: 16,
                                          color: dotColor,
                                          opacity: 0.9,
                                        }}
                                      />
                                    </foreignObject>
                                  </g>
                                </Tooltip>
                              </g>
                            );
                          })}

                          {expectedLine && (
                            <>
                              <line
                                x1={expectedLine.x1}
                                x2={expectedLine.x2}
                                y1={yExpected}
                                y2={yExpected}
                                stroke={EXPECTED_LINE_COLOR}
                                strokeOpacity={isFinished ? 0.3 : 0.9}
                                strokeWidth="4"
                                strokeLinecap="round"
                                style={{ cursor: "pointer" }}
                                onClick={toggleExpanded}
                              />
                              <circle
                                cx={expectedLine.x2}
                                cy={yExpected}
                                r={3}
                                fill={EXPECTED_LINE_COLOR}
                                fillOpacity={isFinished ? 0.3 : 0.9}
                              />
                            </>
                          )}

                          {actualLine && (
                            <>
                              <line
                                x1={actualLine.x1}
                                x2={actualLine.x2}
                                y1={yActual}
                                y2={yActual}
                                stroke={color}
                                strokeOpacity={isFinished ? 0.3 : 1}
                                strokeWidth="6"
                                strokeLinecap="round"
                                style={{ cursor: "pointer" }}
                                onClick={toggleExpanded}
                              />
                              <circle
                                cx={actualLine.x2}
                                cy={yActual}
                                r={4}
                                fill={color}
                                fillOpacity={isFinished ? 0.3 : 1}
                                stroke="#222"
                                strokeWidth="0.8"
                              />
                              <text
                                x={actualLine.x2 + 8}
                                y={yActual + 3}
                                fontSize="10"
                                fill={theme.palette.text.primary}
                              >
                                {actualExtent?.end?.isValid?.()
                                  ? actualExtent.end.format("DD/MM")
                                  : ""}
                              </text>
                            </>
                          )}

                          {!actualLine && expectedLine && (
                            <text
                              x={expectedLine.x2 + 8}
                              y={yExpected + 3}
                              fontSize="10"
                              fill={theme.palette.text.secondary}
                            >
                              {expectedExtent?.end?.isValid?.()
                                ? expectedExtent.end.format("DD/MM")
                                : ""}
                            </text>
                          )}

                          {/* subgantt dinámico */}
                          {expandedRowId === r.id && r.ProductId && (
                            <g
                              transform={`translate(0, ${currentY + ROW_HEIGHT})`}
                            >
                              <foreignObject
                                width={totalWidth}
                                height={subHeights[r.id] || 220}
                                className="gantt-expand"
                                style={{
                                  opacity: 1,
                                  transition:
                                    "height 0.35s ease, opacity 0.35s ease",
                                  overflow: "hidden",
                                  willChange: "height, opacity",
                                }}
                              >
                                <CriticalPathInlineGanttAligned
                                  productId={r.ProductId}
                                  idPresupuesto={
                                    r.idPresupuestoCRM != null &&
                                    Number.isFinite(Number(r.idPresupuestoCRM))
                                      ? Number(r.idPresupuestoCRM)
                                      : null
                                  }
                                  idPresupuestoA3Pedido={
                                    r.IdPresupuestoA3Pedido != null &&
                                    Number.isFinite(
                                      Number(r.IdPresupuestoA3Pedido),
                                    )
                                      ? Number(r.IdPresupuestoA3Pedido)
                                      : null
                                  }
                                  hideLegacyTimeline={Boolean(
                                    r?.HasLegacyLogistics &&
                                      r?.IdPresupuestoA3Pedido != null &&
                                      Number.isFinite(
                                        Number(r.IdPresupuestoA3Pedido),
                                      ),
                                  )}
                                  preferLegacyTimeline={Boolean(
                                    r?.HasLegacyLogistics &&
                                      (r?.IdPresupuestoA3Pedido == null ||
                                        !Number.isFinite(
                                          Number(r.IdPresupuestoA3Pedido),
                                        )),
                                  )}
                                  gridRowSnapshot={r}
                                  clientName={r.CLiente}
                                  scaleX={scaleX}
                                  startMin={startMin}
                                  endMax={endMax}
                                  refreshKey={refreshByProduct[r.id] || 0}
                                  onHeightChange={(h) =>
                                    setSubHeights((prev) => ({
                                      ...prev,
                                      [r.id]: h,
                                    }))
                                  }
                                  onTimelineLoaded={(tasks) =>
                                    setSubTasksByProduct((prev) => ({
                                      ...prev,
                                      [r.id]: tasks,
                                    }))
                                  }
                                />
                              </foreignObject>
                            </g>
                          )}
                        </g>
                      );
                    })}
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>
        )}

      </Box>

      {drawerOpen && (
        <CriticalPathDrawer
          key={`${drawerMode}_${selectedProductId ?? ""}_${selectedMuestraId ?? ""}_${selectedIdPresupuesto ?? ""}_${selectedIdPresupuestoA3Pedido ?? ""}`}
          mode={drawerMode}
          productId={selectedProductId}
          idMuestra={selectedMuestraId}
          idPresupuesto={selectedIdPresupuesto}
          idPresupuestoA3Pedido={selectedIdPresupuestoA3Pedido}
          legacyLogistics={selectedLegacyLogistics}
          hasLegacyLogisticsRow={selectedHasLegacyLogistics}
          gridRowSnapshot={selectedGridRowSnapshot}
          open={drawerOpen}
          onDirty={() => {
            /* No recargar sub-gantt al editar en drawer (evita “No hay tareas”). */
          }}
          onClose={(updatedEntity) => {
            setDrawerOpen(false);
            setSelectedIdPresupuesto(null);
            setSelectedIdPresupuestoA3Pedido(null);
            setSelectedGridRowSnapshot(null);
            setSelectedLegacyLogistics([]);
            setSelectedHasLegacyLogistics(false);

            if (drawerMode === "PRODUCCION" && updatedEntity?.changed) {
              const refreshKey =
                selectedIdPresupuestoA3Pedido != null
                  ? `PEDIDO_${selectedIdPresupuestoA3Pedido}`
                  : selectedProductId != null
                    ? `P_${selectedProductId}`
                    : null;
              if (refreshKey) {
                setTimeout(() => {
                  setRefreshByProduct((p) => ({
                    ...p,
                    [refreshKey]: (p[refreshKey] || 0) + 1,
                  }));
                }, 0);
              }
            }
          }}
        />
      )}

      <CpEntityChat
        open={cpChatState.open}
        productId={cpChatState.productId}
        idPresupuestoA3Pedido={cpChatState.idPresupuestoA3Pedido}
        title={cpChatState.title}
        subtitle={cpChatState.subtitle}
        initialTab={cpChatState.initialTab}
        onNotasChanged={({
          changed,
          productId,
          idMuestra,
          idPresupuestoA3Pedido,
        }) => {
          if (!changed) return;
          const token = localStorage.getItem("token");
          const { userId } = jwtDecode(token);
          getNotasByEntityWithUser({
            productId,
            idMuestra,
            idPresupuestoA3Pedido,
            userId,
          }).then((notas) => {
            const key = getCpNotasEntityKey({
              productId,
              idMuestra,
              idPresupuestoA3Pedido,
            });
            if (key) {
              setNotasCount((prev) => ({
                ...prev,
                [key]: notas.length,
              }));
            }
          });
        }}
        onClose={() => {
          const { productId, idPresupuestoA3Pedido } = cpChatState;
          setCpChatState({
            open: false,
            productId: null,
            idPresupuestoA3Pedido: null,
            title: "",
            subtitle: "",
            initialTab: "notas",
          });
          if (productId || idPresupuestoA3Pedido) {
            const token = localStorage.getItem("token");
            const { userId } = jwtDecode(token);
            getNotasByEntityWithUser({
              productId,
              idMuestra: null,
              idPresupuestoA3Pedido,
              userId,
            }).then((notas) => {
              const key = getCpNotasEntityKey({
                productId,
                idMuestra: null,
                idPresupuestoA3Pedido,
              });
              if (key) {
                setNotasCount((prev) => ({
                  ...prev,
                  [key]: notas.length,
                }));
              }
            });
          }
          loadChatSummary();
        }}
      />

      <Dialog
        open={closeConfirmState.open}
        onClose={() => setCloseConfirmState({ open: false, row: null })}
      >
        <DialogTitle>
          {t("CriticalPathHorizontal.closeDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("CriticalPathHorizontal.closeDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCloseConfirmState({ open: false, row: null })}
          >
            {t("CriticalPathHorizontal.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmCloseEntity}
          >
            {t("CriticalPathHorizontal.closeLine")}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl && hoveredImage)}
        anchorEl={anchorEl}
        anchorReference="anchorEl"
        onClose={() => {
          setAnchorEl(null);
          setHoveredImage(null);
        }}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
      >
        {hoveredImage && (
          <Box sx={{ p: 1 }}>
            <img
              src={hoveredImage}
              alt="preview"
              style={{
                width: 300,
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default CriticalPathHorizontal1;
